import * as React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  FinanceGetDimensionDocument,
  FinanceRenameDimensionDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import type { Dimension } from 'modules/finance/pages/DimensionDetailPage/DimensionDetail/useDimensionDetail'

const showFlash = (type: FlashType, message: string) => {
  app.setFlash({ type, message })
}

const RenameDimensionModal = () => {
  const { company } = useCurrentSession()
  const [newName, setNewName] = React.useState<string>('')
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [renameDimension] = useMutation(FinanceRenameDimensionDocument, {
    extractorFn: (data) => data.financeMutations.renameFinanceCustomDimension,
  })

  const { dimension, onRename, onOpenRenameModal } = useOutletContext<{
    dimension: Dimension
    onRename: (success?: boolean) => void
    onOpenRenameModal: () => void
  }>()

  const goBack = () => {
    navigate(
      navigation.settings.finance.dimensions.dimension.fullPath({
        id: dimension.id,
      })
    )
  }

  const onSubmit = async () => {
    setSubmitting(true)
    const response = await renameDimension({
      variables: {
        id: dimension.id,
        companyId: company.id,
        name: newName,
      },
      refetchQueries: [FinanceGetDimensionDocument],
    })

    const result = handleResult(response, () => {
      showFlash('wrong', i18n.t('finance.modals.rename_dimension.error'))
      setSubmitting(false)
    })

    if (result.success) {
      showFlash('correct', i18n.t('finance.modals.rename_dimension.success'))
      onRename(true)
    }

    goBack()
  }

  const onClose = () => {
    onRename()
    goBack()
  }

  useEffect(() => {
    if (dimension) {
      onOpenRenameModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!dimension) return null

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('finance.modals.rename_dimension.title')}
      description={i18n.t('finance.modals.rename_dimension.description', {
        dimension_name: dimension.name,
      })}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={submitting || !newName.length} submit>
          {i18n.t('finance.buttons.save')}
        </Button>
      }
    >
      <TextField
        label={i18n.t('finance.modals.rename_dimension.name')}
        onChange={setNewName}
        value={newName}
      />
    </Modal>
  )
}

export default observer(RenameDimensionModal)
